import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Combobox,
  TextInput,
  useCombobox,
  CloseButton,
  Group,
  Text,
  Box,
  Badge,
  ScrollArea,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import Fuse, { type IFuseOptions } from "fuse.js";

import { GloveboxDocType, GloveboxDocCategory } from "@/utils/api";
import { useAppEnvironment, useAppEssentialsContext } from "@/contexts";
import { getCategoryColor, getDocTypeIcon } from "@/utils/gloveboxHelpers";
import { LoadingIndicator, AppIcon } from "@/components/generic";
import { GloveboxDocTypePickerModal } from "./GloveboxDocTypePickerModal";

// ============================================
// Types
// ============================================

export interface GloveboxDocTypeSelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected document type ID */
  value?: number | string | null | undefined;

  /** Callback fired when document type selection changes: (name, value, docType?) */
  onChange?: (
    name: string,
    value: number | null,
    docType?: GloveboxDocType
  ) => void;

  /** Placeholder text when no document type is selected */
  placeholder?: string;

  /** Filter by category (vehicle, driver, equipment, other) */
  category?: GloveboxDocCategory | GloveboxDocCategory[];

  /** Custom filter function to filter document types */
  docTypeFilter?: (docType: GloveboxDocType) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Placeholder text shown while loading */
  loadingPlaceholder?: string;

  /** Text shown when no document types are available */
  emptyText?: string;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;

  /** Whether to show category badge in options */
  showCategoryBadge?: boolean;

  /** Whether to group options by category */
  groupByCategory?: boolean;
}

// ============================================
// Constants
// ============================================

// Size configuration for consistent scaling
const ICON_SIZES = {
  inputLeft: 20,
  inputRight: 18,
  categoryHeader: 18,
  optionIcon: 28,
};

// Fuse.js configuration for fuzzy search on doc types
const FUSE_OPTIONS: IFuseOptions<GloveboxDocType> = {
  keys: [
    { name: "name", weight: 0.7 },
    { name: "code", weight: 0.2 },
    { name: "description", weight: 0.1 },
  ],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

// ============================================
// Component
// ============================================

const GloveboxDocTypeSelectorComponent: React.FC<
  GloveboxDocTypeSelectorProps
> = ({
  name = "docTypeId",
  value,
  onChange,
  placeholder,
  category,
  docTypeFilter,
  required = false,
  loadingPlaceholder,
  emptyText,
  error,
  disabled,
  size = "md",
  label,
  showCategoryBadge = false,
  groupByCategory = false,
}) => {
    const { t } = useTranslation(["glovebox", "common"]);
    const { appEssentials, isLoading, lastError, getGloveboxDocTypeById } =
      useAppEssentialsContext();
    const { isMobile } = useAppEnvironment();

    const docTypes: GloveboxDocType[] = appEssentials?.gloveboxDocTypes || [];

    const [modalOpened, setModalOpened] = useState(false);

    const combobox = useCombobox({
      onDropdownClose: () => {
        combobox.resetSelectedOption();
        setSearchValue("");
      },
    });

    const [searchValue, setSearchValue] = useState("");

    // Default translations
    const defaultPlaceholder =
      placeholder || t("glovebox:selectors.docType.placeholder");
    const defaultLabel = label ?? t("glovebox:selectors.docType.label");
    const defaultLoadingPlaceholder =
      loadingPlaceholder || t("glovebox:selectors.docType.loading");
    const defaultEmptyText = emptyText || t("glovebox:selectors.docType.empty");

    // Filter document types by category and custom filter
    const filteredDocTypes = useMemo(() => {
      let filtered = docTypes;

      if (category) {
        const categories = Array.isArray(category) ? category : [category];
        filtered = filtered.filter((dt) =>
          categories.includes(dt.category as GloveboxDocCategory)
        );
      }

      if (docTypeFilter) {
        filtered = filtered.filter(docTypeFilter);
      }

      return [...filtered].sort((a, b) => (a.orderNo || 0) - (b.orderNo || 0));
    }, [docTypes, category, docTypeFilter]);

    // Build Fuse index for fuzzy search
    const fuseIndex = useMemo(
      () => new Fuse(filteredDocTypes, FUSE_OPTIONS),
      [filteredDocTypes]
    );

    // Apply fuzzy search filtering
    const searchedDocTypes = useMemo(() => {
      const search = searchValue.trim();
      if (!search) return filteredDocTypes;
      return fuseIndex.search(search).map((result) => result.item);
    }, [filteredDocTypes, searchValue, fuseIndex]);

    // Group searched doc types by category (preserving order)
    const groupedDocTypes = useMemo(() => {
      if (!groupByCategory) return null;

      const groupMap = new Map<string, GloveboxDocType[]>();

      searchedDocTypes.forEach((docType) => {
        const cat = docType.category || "other";
        if (!groupMap.has(cat)) {
          groupMap.set(cat, []);
        }
        groupMap.get(cat)!.push(docType);
      });

      const groups: { category: string; label: string; items: GloveboxDocType[] }[] = [];
      groupMap.forEach((items, cat) => {
        if (items.length > 0) {
          groups.push({
            category: cat,
            label: t(`glovebox:categories.${cat}`),
            items,
          });
        }
      });

      return groups;
    }, [searchedDocTypes, groupByCategory, t]);

    // Get selected document type
    const selectedDocType = useMemo(() => {
      if (value === null || value === undefined) return null;
      const numericValue =
        typeof value === "string" ? parseInt(value, 10) : value;
      return getGloveboxDocTypeById?.(numericValue) || null;
    }, [value, getGloveboxDocTypeById]);

    // Auto-select first option when required and no value is set
    useEffect(() => {
      if (
        required &&
        (value === null || value === undefined) &&
        filteredDocTypes.length > 0 &&
        onChange
      ) {
        const firstDocType = filteredDocTypes[0];
        if (firstDocType?.id !== undefined) {
          onChange(name, firstDocType.id, firstDocType);
        }
      }
    }, [required, value, filteredDocTypes, onChange, name]);

    // Handle selection (desktop combobox)
    const handleSelect = useCallback(
      (docTypeId: string) => {
        const numericId = parseInt(docTypeId, 10);
        if (isNaN(numericId)) {
          onChange?.(name, null, undefined);
        } else {
          const docType = getGloveboxDocTypeById?.(numericId);
          onChange?.(name, numericId, docType);
        }
        combobox.closeDropdown();
      },
      [name, onChange, getGloveboxDocTypeById, combobox]
    );

    // Handle modal selection
    const handleModalSelect = useCallback(
      (docTypeId: number | null, docType?: GloveboxDocType) => {
        onChange?.(name, docTypeId, docType);
      },
      [name, onChange]
    );

    // Handle clear selection
    const handleClear = useCallback(() => {
      onChange?.(name, null, undefined);
      setSearchValue("");
    }, [name, onChange]);

    // Handle clearing search text (without deselecting)
    const handleClearSearch = useCallback(() => {
      setSearchValue("");
    }, []);

    // Handle search input change
    const handleSearchChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.currentTarget.value);
        combobox.updateSelectedOptionIndex();
      },
      [combobox]
    );

    // Display value in input
    const displayValue = useMemo(() => {
      if (!isMobile && combobox.dropdownOpened) {
        return searchValue;
      }
      return selectedDocType?.name || "";
    }, [isMobile, combobox.dropdownOpened, searchValue, selectedDocType]);

    // Determine component state
    const isLoadingEmpty = isLoading && docTypes.length === 0;
    const isErrorEmpty = Boolean(lastError) && !isLoading && docTypes.length === 0;
    const isEmpty = !isLoading && docTypes.length === 0 && !lastError;
    const isDisabled = disabled || isLoadingEmpty || isErrorEmpty || isEmpty;

    // Placeholder based on state
    const currentPlaceholder = useMemo(() => {
      if (isLoadingEmpty) return defaultLoadingPlaceholder;
      if (isErrorEmpty) return t("common:errors.generic");
      if (isEmpty) return defaultEmptyText;
      return defaultPlaceholder;
    }, [isLoadingEmpty, isErrorEmpty, isEmpty, defaultLoadingPlaceholder, defaultEmptyText, defaultPlaceholder, t]);

    // Error message based on state
    const currentError = useMemo(() => {
      if (isErrorEmpty) return error || t("common:messages.loadError");
      return error;
    }, [isErrorEmpty, error, t]);

    // Left section: selected doc type icon or loading spinner
    const leftSection = useMemo(() => {
      if (isLoading && docTypes.length > 0) {
        return <LoadingIndicator size={size} color="gray" />;
      }
      if (!selectedDocType) return undefined;
      return (
        <AppIcon
          iconName={getDocTypeIcon(selectedDocType.code, selectedDocType.category)}
          size={ICON_SIZES.inputLeft}
        />
      );
    }, [selectedDocType, isLoading, docTypes.length, size]);

    // Right section content (desktop)
    const getRightSection = () => {
      if (isLoadingEmpty) {
        return <LoadingIndicator size={size} color="gray" />;
      }

      if (combobox.dropdownOpened && searchValue) {
        return (
          <CloseButton
            size={size}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClearSearch}
            aria-label={t("common:form.clearSearch")}
          />
        );
      }

      if (selectedDocType && !required && !isDisabled && !combobox.dropdownOpened) {
        return (
          <CloseButton
            size={size}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            aria-label={t("common:form.clearSelection")}
          />
        );
      }

      return <AppIcon iconName="ChevronDown" size={ICON_SIZES.inputRight} />;
    };

    // Right section for mobile trigger input
    const getMobileRightSection = () => {
      if (isLoadingEmpty) {
        return <LoadingIndicator size={size} color="gray" />;
      }

      if (selectedDocType && !required && !isDisabled) {
        return (
          <CloseButton
            size={size}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            aria-label={t("common:form.clearSelection")}
          />
        );
      }

      return <AppIcon iconName="ChevronDown" size={ICON_SIZES.inputRight} />;
    };

    // Check if there are search results
    const hasResults = searchedDocTypes.length > 0;

    // Render a single doc type option (desktop combobox)
    const renderDocTypeOption = (docType: GloveboxDocType) => {
      const numericValue =
        value !== null && value !== undefined
          ? typeof value === "string"
            ? parseInt(value, 10)
            : value
          : null;

      return (
        <Combobox.Option
          key={docType.id}
          value={String(docType.id)}
          active={numericValue === docType.id}
          py="sm"
        >
          <Group gap="sm" wrap="nowrap">
            <AppIcon
              iconName={getDocTypeIcon(docType.code, docType.category)}
              size={ICON_SIZES.optionIcon}
            />
            <Box style={{ flex: 1 }}>
              <Text size="md" fw={500}>
                {docType.name || "Unknown"}
              </Text>
              {docType.description && (
                <Text size="sm" c="dimmed" lineClamp={1}>
                  {docType.description}
                </Text>
              )}
            </Box>
            {showCategoryBadge && (
              <Badge
                size="xs"
                variant="light"
                color={getCategoryColor(docType.category)}
              >
                {t(`glovebox:categories.${docType.category || "other"}`)}
              </Badge>
            )}
          </Group>
        </Combobox.Option>
      );
    };

    // ==========================================
    // Mobile: Read-only input + full-screen modal
    // ==========================================
    if (isMobile) {
      return (
        <>
          {/* Trigger Input — read-only, opens modal on tap */}
          <TextInput
            label={defaultLabel}
            placeholder={currentPlaceholder}
            value={selectedDocType?.name || ""}
            readOnly
            onClick={() => {
              if (!isDisabled) {
                setModalOpened(true);
              }
            }}
            rightSection={getMobileRightSection()}
            rightSectionPointerEvents={
              isLoadingEmpty
                ? "none"
                : selectedDocType && !required && !isDisabled
                  ? "auto"
                  : "none"
            }
            error={currentError}
            required={required}
            disabled={isDisabled}
            size={size}
            leftSection={leftSection}
            styles={{
              input: {
                cursor: isDisabled ? "not-allowed" : "pointer",
                caretColor: "transparent",
              },
            }}
          />

          {/* Full-screen Picker Modal */}
          <GloveboxDocTypePickerModal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            value={
              value !== null && value !== undefined
                ? typeof value === "string"
                  ? parseInt(value, 10)
                  : value
                : null
            }
            onSelect={handleModalSelect}
            category={category}
            docTypeFilter={docTypeFilter}
            showCategoryBadge={showCategoryBadge}
            groupByCategory={groupByCategory}
          />
        </>
      );
    }

    // ==========================================
    // Desktop: Original Combobox behavior
    // ==========================================
    return (
      <Combobox store={combobox} onOptionSubmit={handleSelect} withinPortal>
        <Combobox.Target>
          <TextInput
            label={defaultLabel}
            placeholder={currentPlaceholder}
            value={displayValue}
            onChange={handleSearchChange}
            onClick={() => {
              if (!isDisabled) combobox.openDropdown();
            }}
            onFocus={() => {
              if (!isDisabled) combobox.openDropdown();
            }}
            onBlur={() => {
              combobox.closeDropdown();
              setSearchValue("");
            }}
            rightSection={getRightSection()}
            rightSectionPointerEvents={
              isLoadingEmpty
                ? "none"
                : (combobox.dropdownOpened && searchValue) ||
                  (selectedDocType && !required)
                  ? "auto"
                  : "none"
            }
            error={currentError}
            required={required}
            disabled={isDisabled}
            size={size}
            leftSection={leftSection}
            readOnly={isDisabled}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <ScrollArea.Autosize mah={400} type="scroll">
            <Combobox.Options>
              {/* No results message */}
              {!hasResults && searchValue && (
                <Combobox.Empty>
                  {t("common:form.noOptions")}
                </Combobox.Empty>
              )}

              {/* Grouped options */}
              {groupByCategory && groupedDocTypes
                ? groupedDocTypes.map((group) => (
                  <Combobox.Group
                    key={group.category}
                    label={
                      <Group gap="xs" py={4}>
                        <AppIcon
                          iconName="Folder"
                          size={ICON_SIZES.categoryHeader}
                        />
                        <Text size="md" fw={600} tt="uppercase">
                          {group.label}
                        </Text>
                      </Group>
                    }
                  >
                    {group.items.map(renderDocTypeOption)}
                  </Combobox.Group>
                ))
                : /* Flat list */
                searchedDocTypes.map(renderDocTypeOption)}
            </Combobox.Options>
          </ScrollArea.Autosize>
        </Combobox.Dropdown>
      </Combobox>
    );
  };

export const GloveboxDocTypeSelector = React.memo(
  GloveboxDocTypeSelectorComponent
);